'use client'

import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { X, CheckCircle, AlertCircle, FileText, Mail, MessageSquare } from 'lucide-react'
import { executionsAPI } from '@/lib/api'

export interface PendingStep {
  id: string
  stepNo: number
  stepName: string
  outputData: any
  createdAt: string
  executionId: string
  execution: {
    id: string
    title?: string
    agent: { id: string; name: string; type: string }
    account: { id: string; name: string }
  }
}

interface Props {
  step: PendingStep | null
  onClose: () => void
  onApproved: (stepId: string) => void
}

function detectContentShape(data: any): 'text' | 'html' | 'email' | 'json' | 'empty' {
  if (!data || typeof data !== 'object') return 'empty'
  if (data.subject !== undefined || data.body !== undefined) return 'email'
  if (data.html !== undefined) return 'html'
  if (data.text !== undefined || data.content !== undefined || data.markdown !== undefined) return 'text'
  return 'json'
}

function getTextValue(data: any): string {
  if (!data) return ''
  return data.text ?? data.content ?? data.markdown ?? ''
}

export default function ApprovalDetailModal({ step, onClose, onApproved }: Props) {
  const [editedOutput, setEditedOutput] = useState<any>(null)
  const [approverNote, setApproverNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (step) {
      setEditedOutput(step.outputData ? { ...step.outputData } : {})
      setApproverNote('')
      setError(null)
    }
  }, [step])

  const handleApprove = async () => {
    if (!step) return
    try {
      setSubmitting(true)
      setError(null)
      await executionsAPI.approveStep(step.id, {
        outputData: editedOutput && Object.keys(editedOutput).length > 0 ? editedOutput : undefined,
        approverNote: approverNote.trim() || undefined
      })
      onApproved(step.id)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to approve step')
    } finally {
      setSubmitting(false)
    }
  }

  const contentShape = detectContentShape(step?.outputData)

  const renderContentEditor = () => {
    if (!editedOutput) return null

    switch (contentShape) {
      case 'empty':
        return (
          <p className="text-sm text-gray-500 italic py-4 text-center">
            No output data from the previous step.
          </p>
        )

      case 'text':
        return (
          <textarea
            value={getTextValue(editedOutput)}
            onChange={e => {
              const key = editedOutput.text !== undefined ? 'text'
                : editedOutput.content !== undefined ? 'content' : 'markdown'
              setEditedOutput((prev: any) => ({ ...prev, [key]: e.target.value }))
            }}
            rows={12}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono resize-y"
          />
        )

      case 'html':
        return (
          <div className="space-y-3">
            <div className="border border-gray-200 rounded-md p-3 bg-white max-h-48 overflow-y-auto text-sm">
              <div dangerouslySetInnerHTML={{ __html: editedOutput.html || '' }} />
            </div>
            <textarea
              value={editedOutput.html || ''}
              onChange={e => setEditedOutput((prev: any) => ({ ...prev, html: e.target.value }))}
              rows={8}
              placeholder="Edit HTML..."
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono resize-y"
            />
          </div>
        )

      case 'email':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
              <input
                type="text"
                value={editedOutput.subject || ''}
                onChange={e => setEditedOutput((prev: any) => ({ ...prev, subject: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Body</label>
              <textarea
                value={editedOutput.body || ''}
                onChange={e => setEditedOutput((prev: any) => ({ ...prev, body: e.target.value }))}
                rows={10}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y"
              />
            </div>
          </div>
        )

      case 'json':
      default:
        return (
          <textarea
            value={JSON.stringify(editedOutput, null, 2)}
            onChange={e => {
              try {
                setEditedOutput(JSON.parse(e.target.value))
              } catch {
                // allow invalid JSON while typing
              }
            }}
            rows={12}
            className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono resize-y"
          />
        )
    }
  }

  const contentIcon = contentShape === 'email' ? Mail
    : contentShape === 'html' ? FileText
    : MessageSquare

  const ContentIcon = contentIcon

  return (
    <Transition.Root show={!!step} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-lg bg-white text-left shadow-xl transition-all w-full sm:my-8 sm:max-w-2xl">
                <div className="px-4 pb-4 pt-5 sm:p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between pb-4 border-b">
                    <div className="pr-8">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-6 h-6 rounded-full bg-yellow-100 text-yellow-700 text-xs font-semibold flex items-center justify-center flex-shrink-0">
                          {step?.stepNo}
                        </span>
                        <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                          {step?.stepName || 'Review Step'}
                        </Dialog.Title>
                      </div>
                      <p className="text-sm text-gray-500">
                        {step?.execution.agent.name} &mdash; {step?.execution.account.name}
                      </p>
                    </div>
                    <button
                      onClick={onClose}
                      className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  {/* Error */}
                  {error && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  {/* Content section */}
                  <div className="mt-5 space-y-5 max-h-[55vh] overflow-y-auto pr-1">
                    <div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <ContentIcon className="h-4 w-4 text-gray-500" />
                        <h4 className="text-sm font-medium text-gray-700">Output Content</h4>
                        <span className="text-xs text-gray-400 ml-1">(editable)</span>
                      </div>
                      {renderContentEditor()}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Approver note <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <textarea
                        value={approverNote}
                        onChange={e => setApproverNote(e.target.value)}
                        rows={3}
                        placeholder="Add a note about this approval..."
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3 pt-5 mt-2 border-t">
                    <button
                      onClick={onClose}
                      className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleApprove}
                      disabled={submitting}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                    >
                      {submitting ? (
                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                      Approve &amp; Continue
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
